// /admin/perfis — Perfis e acessos (RBAC configuravel).
// Plano: docs/specs/2026-09-23-rbac-configuravel-plan.md secao 8 (W6).
// So chega aqui quem tem `roles.manage` (rota gateada por CapabilityGuard em
// App.tsx) — sempre super_admin, ou um perfil que ele mesmo concedeu a
// capacidade (na pratica: so super_admin, ja que roles.manage nao e
// concedivel/grantable=false). Por isso esta tela nao precisa de fallback
// pra API antiga: quem a alcanca ja fala com a API nova.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Pencil, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type { AdminRole, CapabilityDef, CreateRoleBody, UpdateRoleBody } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";

const ROLES_KEY = ["admin-roles"];
const CAPS_KEY = ["admin-capabilities"];

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

export function AdminRolesPage(): JSX.Element {
  const qc = useQueryClient();
  const rolesQ = useQuery<AdminRole[]>({
    queryKey: ROLES_KEY,
    queryFn: async () => (await api.get<AdminRole[]>("/api/admin/roles")).data,
  });
  const capsQ = useQuery<CapabilityDef[]>({
    queryKey: CAPS_KEY,
    queryFn: async () => (await api.get<CapabilityDef[]>("/api/admin/capabilities")).data,
  });

  const roles = rolesQ.data ?? [];
  const caps = capsQ.data ?? [];

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const selected = roles.find((r) => r.slug === selectedSlug) ?? roles[0] ?? null;
  if (!selectedSlug && selected) setSelectedSlug(selected.slug);

  // Draft de capacidades marcadas na matriz — ressincroniza quando o perfil
  // selecionado MUDA (nao a cada refetch: senao um PUT em voo perderia a
  // edicao local no meio do caminho).
  const [draftCaps, setDraftCaps] = useState<Set<string>>(new Set());
  const [syncedSlug, setSyncedSlug] = useState<string | null>(null);
  if (selected && syncedSlug !== selected.slug) {
    setDraftCaps(new Set(selected.capabilities));
    setSyncedSlug(selected.slug);
  }

  const isSuperAdminRole = selected?.slug === "super_admin";
  const dirty = !!selected && !isSuperAdminRole && !setsEqual(draftCaps, new Set(selected.capabilities));

  const [createOpen, setCreateOpen] = useState(false);
  const [duplicateOf, setDuplicateOf] = useState<AdminRole | null>(null);
  const [editingMeta, setEditingMeta] = useState<AdminRole | null>(null);
  const [deleting, setDeleting] = useState<AdminRole | null>(null);

  const invalidateRoles = () => void qc.invalidateQueries({ queryKey: ROLES_KEY });

  const saveCapsMut = useMutation({
    mutationFn: async () => {
      if (!selected) return;
      await api.put(`/api/admin/roles/${selected.slug}/capabilities`, {
        capabilities: Array.from(draftCaps),
      });
    },
    onSuccess: () => {
      toast.success("Capacidades salvas");
      invalidateRoles();
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const discardCaps = () => {
    if (selected) setDraftCaps(new Set(selected.capabilities));
  };

  const deleteMut = useMutation({
    mutationFn: async (slug: string) => api.delete(`/api/admin/roles/${slug}`),
    onSuccess: () => {
      toast.success("Perfil excluído");
      setDeleting(null);
      setSelectedSlug(null);
      invalidateRoles();
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err));
      setDeleting(null);
    },
  });

  // "catalogo agrupado por area" (plano secao 7.3): a API pode devolver em
  // qualquer ordem interna — ordena aqui por area+order_n pra a matriz sair
  // sempre na mesma sequencia, area a area.
  const orderedCaps = [...caps].sort((a, b) => a.area.localeCompare(b.area) || a.order_n - b.order_n);
  const areas = Array.from(new Set(orderedCaps.map((c) => c.area)));

  const toggleCap = (key: string, grantable: boolean) => {
    if (!grantable || isSuperAdminRole) return;
    setDraftCaps((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const loading = rolesQ.isLoading || capsQ.isLoading;

  return (
    <div>
      <PageHeader
        title="Perfis e acessos"
        description="Configure quais telas e ações cada tipo de usuário pode acessar."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="icon" aria-hidden /> Novo perfil
          </Button>
        }
      />

      {loading ? (
        <div className="grid-12">
          <div className="gc-4"><Skeleton className="h-64 w-full" /></div>
          <div className="gc-8"><Skeleton className="h-64 w-full" /></div>
        </div>
      ) : rolesQ.isError ? (
        <EmptyState title="Erro ao carregar perfis" description={apiErrorMessage(rolesQ.error)} />
      ) : roles.length === 0 ? (
        <EmptyState title="Nenhum perfil" description="Crie o primeiro perfil." />
      ) : (
        <div className="grid-12" style={{ alignItems: "start" }}>
          {/* Mobile: select no topo (spec do plano — coluna de perfis vira select) */}
          <div className="gc-12 md:hidden field">
            <Label htmlFor="role-picker-mobile">Perfil</Label>
            <Select
              id="role-picker-mobile"
              value={selected?.slug ?? ""}
              onChange={(e) => setSelectedSlug(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.name} · {r.user_count} usuário{r.user_count === 1 ? "" : "s"}
                </option>
              ))}
            </Select>
          </div>

          {/* Desktop: coluna de perfis */}
          <div className="gc-4 hidden md:block">
            <div className="card" style={{ padding: 0 }}>
              <div className="stack" style={{ padding: ".5rem" }}>
                {roles.map((r) => (
                  <button
                    key={r.slug}
                    type="button"
                    onClick={() => setSelectedSlug(r.slug)}
                    className="row"
                    style={{
                      gridTemplateColumns: "1fr auto",
                      padding: ".5rem .75rem",
                      borderRadius: "var(--radius-md)",
                      border: "none",
                      background: r.slug === selected?.slug ? "var(--color-coral-soft)" : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                      <span className="row__title truncate">{r.name}</span>
                      {r.description ? (
                        <span className="row__sub truncate" style={{ fontFamily: "inherit" }}>{r.description}</span>
                      ) : null}
                    </span>
                    <span className="cluster" style={{ flexShrink: 0 }}>
                      {r.is_system ? <span className="chip">Sistema</span> : null}
                      <span className="chip chip--mono">{r.user_count}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Painel do perfil selecionado — .gc-8 ja colapsa pra span 12
              abaixo de 60rem (regra existente em index.css), o mesmo
              breakpoint que o resto do app usa pra layout de 2 colunas. */}
          <div className="gc-8">
            {selected ? (
              <div className="card stack">
                <div className="spread">
                  <div className="min-w-0">
                    <div className="cluster">
                      <span className="card__title truncate">{selected.name}</span>
                      {selected.is_system ? <span className="chip">Sistema</span> : null}
                    </div>
                    {selected.description ? (
                      <p className="card__meta" style={{ marginTop: ".125rem" }}>{selected.description}</p>
                    ) : null}
                  </div>
                  <div className="cluster" style={{ flexShrink: 0 }}>
                    <Button variant="secondary" size="sm" onClick={() => setEditingMeta(selected)}>
                      <Pencil className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden /> Editar
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setDuplicateOf(selected)}>
                      <Copy className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden /> Duplicar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={selected.is_system || selected.user_count > 0}
                      title={
                        selected.is_system
                          ? "perfil do sistema"
                          : selected.user_count > 0
                            ? `${selected.user_count} usuário${selected.user_count === 1 ? "" : "s"} usam este perfil`
                            : "Excluir"
                      }
                      onClick={() => setDeleting(selected)}
                    >
                      <Trash2 className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden /> Excluir
                    </Button>
                  </div>
                </div>

                {isSuperAdminRole ? (
                  <div className="cluster field__hint" style={{ background: "var(--color-soft)", borderRadius: "var(--radius-md)", padding: ".5rem .75rem" }}>
                    <ShieldAlert className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden />
                    Super Admin sempre tem tudo — capacidades travadas por regra de negócio (RN23), não editáveis aqui.
                  </div>
                ) : null}

                <div className="stack" style={{ gap: "1rem" }}>
                  {areas.map((area) => (
                    <div key={area} className="stack" style={{ gap: ".25rem" }}>
                      <div className="filters__group-label">{area}</div>
                      <div className="card" style={{ padding: 0 }}>
                        <div className="stack" style={{ gap: 0 }}>
                          {orderedCaps
                            .filter((c) => c.area === area)
                            .map((c) => {
                              const checked = isSuperAdminRole || (c.grantable && draftCaps.has(c.key));
                              const disabled = isSuperAdminRole || !c.grantable;
                              return (
                                <div
                                  key={c.key}
                                  className="row"
                                  style={{ gridTemplateColumns: "1fr auto", padding: ".625rem .75rem" }}
                                >
                                  <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                                    <span className="row__title truncate" style={{ fontSize: "var(--text-base)" }}>{c.label}</span>
                                    <span className="row__sub truncate" style={{ fontFamily: "inherit" }}>
                                      {!c.grantable ? "Somente Super Admin concede esta capacidade. " : ""}
                                      {c.description}
                                    </span>
                                  </span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={checked}
                                    aria-label={c.label}
                                    disabled={disabled}
                                    onClick={() => toggleCap(c.key, c.grantable)}
                                    className="switch"
                                    style={{ background: checked ? "var(--color-coral)" : "var(--color-line)" }}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {dirty ? (
        <div
          className="spread"
          style={{
            position: "sticky",
            bottom: 0,
            marginTop: "1rem",
            background: "var(--color-card)",
            boxShadow: "var(--shadow-card)",
            borderRadius: "var(--radius-lg)",
            padding: ".75rem 1rem",
          }}
        >
          <span className="row__title">Alterações não salvas</span>
          <span className="cluster">
            <Button variant="secondary" onClick={discardCaps} disabled={saveCapsMut.isPending}>
              Descartar
            </Button>
            <Button onClick={() => saveCapsMut.mutate()} loading={saveCapsMut.isPending}>
              Salvar
            </Button>
          </span>
        </div>
      ) : null}

      <RoleFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(slug) => setSelectedSlug(slug)}
      />
      {duplicateOf ? (
        <RoleFormDialog
          open
          copyFrom={duplicateOf}
          onOpenChange={(o) => {
            if (!o) setDuplicateOf(null);
          }}
          onCreated={(slug) => {
            setSelectedSlug(slug);
            setDuplicateOf(null);
          }}
        />
      ) : null}
      {editingMeta ? (
        <EditRoleMetaDialog
          role={editingMeta}
          onOpenChange={(o) => {
            if (!o) setEditingMeta(null);
          }}
        />
      ) : null}
      {deleting ? (
        <Dialog open onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Excluir perfil "{deleting.name}"?</DialogTitle>
              <DialogDescription>
                Esta ação não pode ser desfeita. Usuários com este perfil precisam ser
                movidos para outro perfil antes.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary">Cancelar</Button>
              </DialogClose>
              <Button
                variant="destructive"
                loading={deleteMut.isPending}
                onClick={() => deleteMut.mutate(deleting.slug)}
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function RoleFormDialog({
  open,
  onOpenChange,
  onCreated,
  copyFrom,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (slug: string) => void;
  copyFrom?: AdminRole;
}): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (cópia)` : "");
  const [description, setDescription] = useState(copyFrom?.description ?? "");

  const mut = useMutation({
    mutationFn: async () => {
      const body: CreateRoleBody = {
        name: name.trim(),
        description: description.trim() || undefined,
        copy_from: copyFrom?.slug,
      };
      const r = await api.post<AdminRole>("/api/admin/roles", body);
      return r.data;
    },
    onSuccess: (role) => {
      toast.success("Perfil criado");
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      onCreated(role.slug);
      onOpenChange(false);
      setName("");
      setDescription("");
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copyFrom ? `Duplicar "${copyFrom.name}"` : "Novo perfil"}</DialogTitle>
          <DialogDescription>
            {copyFrom
              ? "Cria um perfil novo com as mesmas capacidades, que você pode ajustar depois."
              : "Cria um perfil sem nenhuma capacidade — marque o que ele pode fazer na matriz."}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            mut.mutate();
          }}
          className="stack"
        >
          <div className="field">
            <Label htmlFor="rf-name">Nome</Label>
            <Input id="rf-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <Label htmlFor="rf-desc">Descrição (opcional)</Label>
            <Textarea id="rf-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={mut.isPending} disabled={!name.trim()}>
              {copyFrom ? "Duplicar" : "Criar perfil"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRoleMetaDialog({
  role,
  onOpenChange,
}: {
  role: AdminRole;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);

  const mut = useMutation({
    mutationFn: async () => {
      const body: UpdateRoleBody = {};
      if (name !== role.name) body.name = name.trim();
      if (description !== role.description) body.description = description.trim();
      if (Object.keys(body).length > 0) {
        await api.patch(`/api/admin/roles/${role.slug}`, body);
      }
    },
    onSuccess: () => {
      toast.success("Perfil atualizado");
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar perfil</DialogTitle>
          <DialogDescription>{role.slug}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
          className="stack"
        >
          <div className="field">
            <Label htmlFor="rm-name">Nome</Label>
            <Input id="rm-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <Label htmlFor="rm-desc">Descrição</Label>
            <Textarea id="rm-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={mut.isPending}>Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
